import { contrast, HEX_RE, normalizeHex, readableOn } from './color.js';

/**
 * A badge is DATA, not an image: a small spec stored with the team, competition
 * or league and drawn as SVG wherever it is shown. Nothing here copies a real
 * crest or logo; shapes, patterns and emblems are generic heraldry.
 */
export const BADGE_KINDS = ['team', 'competition', 'league'] as const;
export const SHAPES = ['shield', 'heater', 'pointed', 'round', 'hex', 'diamond', 'banner', 'square'] as const;
export const PATTERNS = [
  'plain',
  'stripes',
  'pinstripes',
  'hoops',
  'halves',
  'quarters',
  'sash',
  'chevron',
  'cross',
  'checks',
  'band',
  'border',
] as const;
export const EMBLEMS = [
  'none',
  'star',
  'stars3',
  'ball',
  'crown',
  'bolt',
  'wave',
  'tower',
  'oak',
  'anchor',
  'wings',
  'trophy',
  'ring',
  'arrow',
] as const;
/** stack: emblem above a name plate. emblem: one large emblem. monogram: large letters only. */
export const LAYOUTS = ['stack', 'emblem', 'monogram'] as const;

export type BadgeKind = (typeof BADGE_KINDS)[number];
export type Shape = (typeof SHAPES)[number];
export type Pattern = (typeof PATTERNS)[number];
export type Emblem = (typeof EMBLEMS)[number];
export type Layout = (typeof LAYOUTS)[number];

export interface BadgeColors {
  /** Main field colour (#RRGGBB). */
  primary: string;
  /** Pattern and trim colour. */
  secondary: string;
  /** Emblem colour. */
  accent: string;
}

export interface BadgeSpec extends BadgeColors {
  v: 1;
  kind: BadgeKind;
  shape: Shape;
  pattern: Pattern;
  emblem: Emblem;
  layout: Layout;
  /** 1 to 3 capital letters or digits, e.g. "NB". */
  text: string;
  /** What the random choices were derived from; changing it "re-rolls" the design. */
  seed: string;
}

export interface BadgeInput {
  kind: BadgeKind;
  name: string;
  shortName?: string | null;
  seed?: string;
  primary?: string | null;
  secondary?: string | null;
  accent?: string | null;
}

/** Colour sets that read as football kits without belonging to any club. */
export const PALETTE: readonly (readonly [string, string, string])[] = [
  ['#C8102E', '#FFFFFF', '#111827'],
  ['#1D4ED8', '#FFFFFF', '#F5B544'],
  ['#7A1E3A', '#8EC5E8', '#F5D76E'],
  ['#111111', '#FFFFFF', '#C8102E'],
  ['#F2C230', '#111111', '#FFFFFF'],
  ['#136F3A', '#FFFFFF', '#F5B544'],
  ['#E35205', '#111111', '#FFFFFF'],
  ['#0B1F4B', '#E0B04A', '#FFFFFF'],
  ['#5B2A86', '#FFFFFF', '#F5B544'],
  ['#6CB4E4', '#0B1F4B', '#FFFFFF'],
  ['#6B0F1A', '#D4A537', '#FFFFFF'],
  ['#0F766E', '#FFFFFF', '#F2C230'],
  ['#FFFFFF', '#C8102E', '#111111'],
  ['#FFFFFF', '#0B1F4B', '#C8102E'],
  ['#B91C1C', '#111111', '#FFFFFF'],
  ['#1E3A8A', '#C8102E', '#FFFFFF'],
  ['#14532D', '#F2C230', '#FFFFFF'],
  ['#9D174D', '#FFFFFF', '#111111'],
  ['#374151', '#F97316', '#FFFFFF'],
  ['#0369A1', '#F2C230', '#FFFFFF'],
];

/** FNV-1a, 32-bit. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32: small, fast, good enough for picking designs. */
function rngFrom(seed: string): () => number {
  let a = hashString(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rng: () => number, weighted: readonly (readonly [T, number])[]): T {
  const total = weighted.reduce((s, [, w]) => s + w, 0);
  let r = rng() * total;
  for (const [v, w] of weighted) {
    r -= w;
    if (r < 0) return v;
  }
  return weighted[weighted.length - 1]![0];
}

const W = {
  team: {
    shape: [['shield', 5], ['heater', 4], ['pointed', 3], ['round', 3], ['hex', 1], ['banner', 2], ['square', 1]],
    pattern: [
      ['plain', 3], ['stripes', 3], ['pinstripes', 1], ['hoops', 2], ['halves', 2], ['quarters', 1],
      ['sash', 2], ['chevron', 2], ['cross', 1], ['checks', 1], ['band', 2], ['border', 1],
    ],
    emblem: [
      ['star', 2], ['stars3', 1], ['ball', 3], ['crown', 1], ['bolt', 1], ['wave', 1], ['tower', 2],
      ['oak', 2], ['anchor', 1], ['wings', 1], ['ring', 1], ['arrow', 1],
    ],
    layout: [['stack', 6], ['emblem', 2], ['monogram', 2]],
  },
  competition: {
    shape: [['round', 4], ['hex', 2], ['banner', 2], ['square', 2], ['shield', 1]],
    pattern: [['plain', 3], ['band', 2], ['border', 3], ['hoops', 1], ['checks', 1]],
    emblem: [['trophy', 4], ['star', 2], ['stars3', 2], ['ball', 2], ['crown', 2], ['ring', 1]],
    layout: [['stack', 5], ['emblem', 3], ['monogram', 1]],
  },
  league: {
    shape: [['round', 3], ['diamond', 2], ['hex', 2], ['square', 2], ['banner', 1]],
    pattern: [['plain', 2], ['stripes', 1], ['hoops', 1], ['sash', 2], ['chevron', 2], ['border', 2], ['band', 1]],
    emblem: [['star', 2], ['bolt', 2], ['wave', 1], ['arrow', 2], ['ball', 2], ['ring', 1], ['wings', 1], ['oak', 1]],
    layout: [['stack', 4], ['emblem', 2], ['monogram', 3]],
  },
} as const satisfies Record<
  BadgeKind,
  {
    shape: readonly (readonly [Shape, number])[];
    pattern: readonly (readonly [Pattern, number])[];
    emblem: readonly (readonly [Emblem, number])[];
    layout: readonly (readonly [Layout, number])[];
  }
>;

const STOP_TOKENS = new Set(['FC', 'AFC', 'CF', 'SC', 'AC', 'CD', 'FK', 'SK', 'THE', 'DE', 'CLUB', 'OF']);
export const TEXT_RE = /^[\p{Lu}\p{N}]{1,3}$/u;

/** Initials for the name plate: "Ashford Athletic" → "AA", "Northbridge" → "NOR", short name "NBR" → "NBR". */
export function monogram(name: string, shortName?: string | null): string {
  const tokensOf = (s: string) =>
    s
      .toLocaleUpperCase('en')
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .split(/[^\p{Lu}\p{N}]+/u)
      .filter(Boolean);
  const short = shortName ? tokensOf(shortName) : [];
  if (short.length === 1 && short[0]!.length <= 3) return short[0]!;
  const all = tokensOf(name);
  const significant = all.filter((t) => !STOP_TOKENS.has(t));
  const tokens = significant.length ? significant : all;
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) return tokens[0]!.slice(0, 3);
  return tokens
    .slice(0, 3)
    .map((t) => t[0])
    .join('');
}

function cleanColor(v: string | null | undefined): string | null {
  return v ? normalizeHex(v) : null;
}

/** Fills in missing colours and keeps the three of them distinguishable. */
export function resolveColors(rng: () => number, input: Partial<BadgeColors>): BadgeColors {
  const base = PALETTE[Math.floor(rng() * PALETTE.length)]!;
  const primary = cleanColor(input.primary) ?? base[0];
  let secondary = cleanColor(input.secondary) ?? base[1];
  if (contrast(primary, secondary) < 1.6) secondary = readableOn(primary, [], 99);
  let accent = cleanColor(input.accent) ?? base[2];
  if (contrast(primary, accent) < 1.6) accent = secondary;
  return { primary, secondary, accent };
}

/** Deterministic: the same input always gives the same badge. */
export function generateBadge(input: BadgeInput): BadgeSpec {
  const seed = input.seed ?? `${input.kind}:${input.name.trim().toLowerCase()}`;
  const rng = rngFrom(seed);
  const colors = resolveColors(rng, {
    primary: input.primary ?? undefined,
    secondary: input.secondary ?? undefined,
    accent: input.accent ?? undefined,
  });
  const w = W[input.kind];
  const shape = pick(rng, w.shape);
  const pattern = pick(rng, w.pattern);
  const emblem = pick(rng, w.emblem);
  const layout = pick(rng, w.layout);
  return {
    v: 1,
    kind: input.kind,
    shape,
    pattern,
    emblem,
    layout,
    text: monogram(input.name, input.shortName),
    seed,
    ...colors,
  };
}

/** New shape, pattern, emblem and layout; same colours and letters. */
export function rerollBadge(spec: BadgeSpec, salt: string | number = Date.now()): BadgeSpec {
  const seed = `${spec.seed.split('#')[0]}#${salt}`;
  const rng = rngFrom(seed);
  const w = W[spec.kind];
  return {
    ...spec,
    seed,
    shape: pick(rng, w.shape),
    pattern: pick(rng, w.pattern),
    emblem: pick(rng, w.emblem),
    layout: pick(rng, w.layout),
  };
}

const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === 'string' && (list as readonly string[]).includes(v);

/**
 * Validates a spec coming from a client (admin console). Returns a clean copy,
 * or the list of problems. Only known enum values, #RRGGBB colours and 1 to 3
 * capitals or digits are accepted, so nothing user-typed ever reaches the SVG
 * markup unescaped.
 */
export function parseBadge(x: unknown): { ok: true; spec: BadgeSpec } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (typeof x !== 'object' || x === null) return { ok: false, errors: ['badge must be an object'] };
  const o = x as Record<string, unknown>;
  if (!isOneOf(BADGE_KINDS, o.kind)) errors.push('kind is invalid');
  if (!isOneOf(SHAPES, o.shape)) errors.push('shape is invalid');
  if (!isOneOf(PATTERNS, o.pattern)) errors.push('pattern is invalid');
  if (!isOneOf(EMBLEMS, o.emblem)) errors.push('emblem is invalid');
  if (!isOneOf(LAYOUTS, o.layout)) errors.push('layout is invalid');
  const colors: Partial<BadgeColors> = {};
  for (const k of ['primary', 'secondary', 'accent'] as const) {
    const c = typeof o[k] === 'string' ? normalizeHex(o[k] as string) : null;
    if (!c || !HEX_RE.test(c)) errors.push(`${k} must be a #RRGGBB colour`);
    else colors[k] = c;
  }
  const text = typeof o.text === 'string' ? o.text.trim().toLocaleUpperCase('en') : '';
  if (!TEXT_RE.test(text)) errors.push('text must be 1 to 3 capital letters or digits');
  const seed = typeof o.seed === 'string' && o.seed.length <= 200 ? o.seed : null;
  if (seed === null) errors.push('seed must be a string of at most 200 characters');
  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    spec: {
      v: 1,
      kind: o.kind as BadgeKind,
      shape: o.shape as Shape,
      pattern: o.pattern as Pattern,
      emblem: o.emblem as Emblem,
      layout: o.layout as Layout,
      text,
      seed: seed!,
      primary: colors.primary!,
      secondary: colors.secondary!,
      accent: colors.accent!,
    },
  };
}

/** Short, stable fingerprint for cache-busting URLs (`?v=`). */
export function badgeVersion(spec: BadgeSpec): string {
  const keys = ['v', 'kind', 'shape', 'pattern', 'emblem', 'layout', 'text', 'primary', 'secondary', 'accent'] as const;
  return hashString(keys.map((k) => String(spec[k])).join('|')).toString(36);
}
